import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/dateFormat'
import { useUsers } from '@/hooks/useUsers'
import { useAuth } from '@/auth/useAuth'
import { ListParams, UserRole } from '@/types/index'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { deactivateUser } from '@/api/users'
import { Search, AlertCircle } from 'lucide-react'
import { DEFAULT_PAGE_SIZE } from '@/api/paging'

interface User {
  id: string
  displayName: string
  email: string
  department?: string
  jobTitle?: string
  status: 'Active' | 'Inactive'
  lastLogin?: string
}

const UserList: React.FC = () => {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Inactive'>('All')
  const [page, setPage] = useState(1)

  // Debounced search — the term is sent to the server, not applied to the page.
  //
  // WI #337: the page number is reset in the same tick as the new search term.
  // Any change to the query invalidates the current page (narrowing a filter
  // while on page 3 would otherwise land on an empty page with no way back),
  // and doing it here rather than in a follow-up effect means React batches
  // both updates into one render — so one request goes out, not two.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // WI #337: memoised because `useUsers` re-fetches on the identity of this
  // object — an inline literal would refetch on every render, forever.
  //
  // `GET /users` returns active users only unless asked otherwise, so 'All'
  // and 'Inactive' both need `include_inactive`.
  const listParams = useMemo<ListParams>(
    () => ({
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(statusFilter === 'All' ? { includeInactive: true } : {}),
      ...(statusFilter === 'Inactive' ? { includeInactive: true, isActive: false } : {}),
    }),
    [page, debouncedSearch, statusFilter],
  )

  const { users: hookUsers = [], total, isLoading, error, refetch } = useUsers(listParams)
  const [isSyncing, setIsSyncing] = useState(false)
  const [confirmDeactivate, setConfirmDeactivate] = useState<{ userId: string; userName: string } | null>(null)
  const [isDeactivating, setIsDeactivating] = useState(false)
  const [deactivateError, setDeactivateError] = useState<string | null>(null)

  // Bug #143: the confirm button's onClick used to only call
  // setConfirmDeactivate(null), never hitting the API. This actually performs
  // the deactivation and refreshes the list so the row moves from Active→Inactive.
  const handleConfirmDeactivate = useCallback(async () => {
    if (!confirmDeactivate) return
    setIsDeactivating(true)
    setDeactivateError(null)
    try {
      await deactivateUser(confirmDeactivate.userId)
      setConfirmDeactivate(null)
      await refetch()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to deactivate user'
      setDeactivateError(message)
    } finally {
      setIsDeactivating(false)
    }
  }, [confirmDeactivate, refetch])

  const isAdmin = currentUser?.roles?.includes(UserRole.PlatformAdmin)

  // Adapt hook users to our interface. WI #337: this is the server's page of
  // results verbatim — the search and status filters are query params now, so
  // there is deliberately no client-side filtering left here. Filtering a
  // single page never made sense once the page was one of several.
  const users: User[] = useMemo(
    () =>
      hookUsers.map((u) => ({
        id: u.id,
        displayName: u.displayName,
        email: u.email,
        department: u.department,
        jobTitle: u.jobTitle,
        status: u.isActive ? 'Active' : 'Inactive',
        lastLogin: u.lastLoginAt,
      })),
    [hookUsers],
  )

  const handleSync = async () => {
    if (!isAdmin) return
    setIsSyncing(true)
    try {
      // Call sync endpoint
      setTimeout(() => setIsSyncing(false), 1000)
    } finally {
      setIsSyncing(false)
    }
  }

  const handleView = (userId: string) => {
    navigate(`/users/${userId}`)
  }

  const columns = [
    {
      key: 'displayName' as keyof User,
      header: 'Display name',
      render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div>,
    },
    { key: 'email' as keyof User, header: 'Email', render: (value: string) => value },
    { key: 'department' as keyof User, header: 'Department', render: (value?: string) => value || '-' },
    { key: 'jobTitle' as keyof User, header: 'Job title', render: (value?: string) => value || '-' },
    {
      key: 'status' as keyof User,
      header: 'Status',
      render: (value: string) => (
        <span
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: value === 'Active' ? '#dbeafe' : '#f3f4f6',
            color: value === 'Active' ? '#0284c7' : '#6b7280',
          }}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'lastLogin' as keyof User,
      header: 'Last login',
      render: (value?: string) => value ? formatDate(value) : 'Never',
    },
  ]

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="Users"
        actions={
          isAdmin ? (
            <button
              onClick={handleSync}
              disabled={isSyncing}
              style={{
                padding: '8px 16px',
                backgroundColor: isSyncing ? 'var(--color-light-grey)' : 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isSyncing ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              {isSyncing ? 'Syncing...' : 'Sync users'}
            </button>
          ) : undefined
        }
      />

      <div style={{ padding: '24px' }}>
        {error && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Error loading users</p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error?.message ?? 'An unexpected error occurred'}</p>
            </div>
          </div>
        )}

        {/* Search and filter bar */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--color-dark-grey)' }} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'All' | 'Active' | 'Inactive')
              setPage(1)
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
            }}
          >
            <option value="All">All status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* Data table */}
        {isLoading && users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            Loading users...
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            {searchTerm || statusFilter !== 'All' ? 'No users match your search.' : 'No users found.'}
          </div>
        ) : (
          // WI #337: moved off DataTableAdapter, which has no pagination props
          // at all, onto the shared DataTable so this list can page like the
          // others. `hidden` replaces the adapter's per-row actions callback.
          <DataTable
            columns={columns}
            data={users}
            rowKey="id"
            isLoading={isLoading}
            totalItems={total}
            currentPage={page}
            pageSize={DEFAULT_PAGE_SIZE}
            onPageChange={setPage}
            onRowClick={(row: User) => handleView(row.id)}
            actions={[
              { label: 'View', onClick: (row: User) => handleView(row.id) },
              {
                label: 'Deactivate',
                variant: 'danger' as const,
                hidden: (row: User) => !isAdmin || row.status !== 'Active',
                onClick: (row: User) =>
                  setConfirmDeactivate({ userId: row.id, userName: row.displayName }),
              },
            ]}
          />
        )}
      </div>

      {/* Deactivate confirmation modal */}
      {confirmDeactivate && (
        <Modal
          isOpen={!!confirmDeactivate}
          title="Deactivate user"
          onClose={() => {
            if (isDeactivating) return
            setConfirmDeactivate(null)
            setDeactivateError(null)
          }}
          preventClose={isDeactivating}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  if (isDeactivating) return
                  setConfirmDeactivate(null)
                  setDeactivateError(null)
                }}
                disabled={isDeactivating}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--color-white)',
                  cursor: isDeactivating ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family)',
                  opacity: isDeactivating ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeactivate}
                disabled={isDeactivating}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isDeactivating ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family)',
                  opacity: isDeactivating ? 0.6 : 1,
                }}
              >
                {isDeactivating ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          }
        >
          <p style={{ margin: 0, color: 'var(--color-dark-grey)' }}>
            Are you sure you want to deactivate <strong>{confirmDeactivate.userName}</strong>? This action can be reversed.
          </p>
          {deactivateError && (
            <p style={{ marginTop: '12px', color: '#dc2626', fontSize: '13px' }}>
              {deactivateError}
            </p>
          )}
        </Modal>
      )}
    </div>
  )
}

export default UserList
